// BulkImportGuideDialog.jsx
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import attachmentInstruction from '../images/attachment-instruction.png'
import {WhatsApp} from "@mui/icons-material";

const BRAND = "#0A564B";

export default function AttachmentInstructionModal({
                                                  open,
                                                  onClose,
                                              }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            id="attachment_instruction"
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { overflow: "hidden", borderRadius: fullScreen ? 0 : 3 } }}
        >
            <DialogTitle
                sx={{
                    py: 1.25,
                    background: BRAND,
                    color: "#fff",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1, letterSpacing: 0.2 }}>
                        Import Data in Bulk
                    </Typography>

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mr: 1 }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: "rgba(255,255,255,0.85)", fontStyle: "italic" }}
                        >
                            Got questions?
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<WhatsApp />}
                            href="https://wa.me/6282228886906?text=Hello%20Trucarbon,%20I%20need%20help%20with%20file%20uploads"
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                                bgcolor: "#E17E7F",
                                "&:hover": { bgcolor: "#1ebe5d" },
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            Contact Us
                        </Button>
                    </Stack>

                    <IconButton onClick={onClose} size="small" sx={{ color: "#fff", ml: 1 }} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 0,
                    height: fullScreen ? "calc(100dvh - 56px)" : "80vh",
                    background: "#fff",
                }}
            >
                <Box

                    sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, overflowY: "auto", height: "100%" }}>
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="no-supporting-content" id="no-supporting-header">
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Without any supporting documents
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1.5}>
                                <Typography>
                                    <strong>Step 1:</strong> Fill in all necessary data
                                </Typography>
                                <Typography>
                                    <strong>Step 2:</strong> Download template. Sample data is in the <em>“Example”</em> sheet
                                </Typography>
                                <Typography>
                                    <strong>Step 3:</strong> Compress file into .zip
                                </Typography>

                                <Box sx={{ pl: 2 }}>
                                    <Typography sx={{ fontWeight: 600 }}>On Windows</Typography>
                                    <ol style={{ marginTop: 4 }}>
                                        <li>Locate the file or folder that you want to zip.</li>
                                        <li>
                                            Press and hold (or right-click) the file or folder, select (or point to) <strong>Send to</strong>,
                                            and then select <strong>Compressed (zipped) folder</strong>.
                                        </li>
                                    </ol>
                                    <Typography sx={{ mt: 0.5 }}>
                                        A new zipped folder with the same name is created in the same location. To rename it, press and hold
                                        (or right-click) the folder, select <strong>Rename</strong>, and then type the new name.
                                    </Typography>

                                    <Typography sx={{ fontWeight: 600, mt: 1.5 }}>On Mac</Typography>
                                    <ol style={{ marginTop: 4 }}>
                                        <li>
                                            <em>Compress a file or folder:</em> Control-click it or tap it using two fingers, then choose{" "}
                                            <strong>Compress</strong> from the shortcut menu.
                                        </li>
                                        <li>
                                            If you compress a single item, the compressed file has the name of the original item with the{" "}
                                            .zip extension. If you compress multiple items at once, the compressed file is called{" "}
                                            <strong>Archive.zip</strong>.
                                        </li>
                                    </ol>
                                </Box>

                                <Typography>
                                    <strong>Step 4:</strong> Upload the zip file by clicking the <em>Choose File</em> button
                                </Typography>
                                <Typography>
                                    <strong>Step 5:</strong> Click <em>Save</em>
                                </Typography>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    <Divider sx={{ my: 2 }} />

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="with-supporting-content" id="with-supporting-header">
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                With supporting documents
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing={1.5}>
                                <Typography>
                                    <strong>Step 1:</strong> Fill in all necessary data
                                </Typography>
                                <Typography>
                                    <strong>Step 2:</strong> Download template. Sample data is in the <em>“Example”</em> sheet
                                </Typography>

                                <Box>
                                    <Typography>
                                        <strong>Step 3:</strong> Add Supporting Documents
                                    </Typography>
                                    <ul style={{ marginTop: 6 }}>
                                        <li>It will be stored as proof of records for audit trails</li>
                                        <li>Examples of it: electricity bills, payment receipt of fuels, etc</li>
                                        <li>
                                            Rename it with the following format: <strong>“attachment-[row number][format]”</strong>
                                        </li>
                                        <li>
                                            Example: The supporting document for an item in Row Number 1 must be saved with the name:{" "}
                                            <strong>“attachment-1.jpg”</strong>
                                        </li>
                                    </ul>

                                    <Box sx={{ my: 1.5, border: "1px solid #eee", borderRadius: 1.5, p: 1.5, bgcolor: "#fafafa" }}>
                                        <Box
                                            component="img"
                                            src={attachmentInstruction}
                                            alt="Example sheet"
                                            sx={{
                                                width: "100%",
                                                height: "auto",
                                                display: "block",
                                                borderRadius: 1,
                                            }}
                                        />
                                    </Box>

                                    <Typography sx={{ mt: 0.5 }}>
                                        Mark <strong>“Has Attachment”</strong> column associated with it as <strong>“yes”</strong>
                                    </Typography>
                                </Box>

                                <Typography>
                                    <strong>Step 4:</strong> Compress all files into .zip
                                </Typography>

                                <Box sx={{ pl: 2 }}>
                                    <Typography sx={{ fontWeight: 600 }}>On Windows</Typography>
                                    <ol style={{ marginTop: 4 }}>
                                        <li>Locate the file or folder that you want to zip.</li>
                                        <li>
                                            Press and hold (or right-click) the file or folder, select (or point to) <strong>Send to</strong>,
                                            and then select <strong>Compressed (zipped) folder</strong>.
                                        </li>
                                    </ol>
                                    <Typography sx={{ mt: 0.5 }}>
                                        A new zipped folder with the same name is created in the same location. To rename it, press and hold
                                        (or right-click) the folder, select <strong>Rename</strong>, and then type the new name.
                                    </Typography>

                                    <Typography sx={{ fontWeight: 600, mt: 1.5 }}>On Mac</Typography>
                                    <ol style={{ marginTop: 4 }}>
                                        <li>
                                            <em>Compress a file or folder:</em> Control-click it or tap it using two fingers, then choose{" "}
                                            <strong>Compress</strong> from the shortcut menu.
                                        </li>
                                        <li>
                                            If you compress a single item, the compressed file has the name of the original item with the{" "}
                                            .zip extension. If you compress multiple items at once, the compressed file is called{" "}
                                            <strong>Archive.zip</strong>.
                                        </li>
                                    </ol>
                                </Box>

                                <Typography>
                                    <strong>Step 5:</strong> Upload the zip file by clicking the <em>Choose File</em> button
                                </Typography>
                                <Typography>
                                    <strong>Step 6:</strong> Click <em>Save</em>
                                </Typography>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
